import React from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Full-screen fallback shown whenever the auth layer is in a non-rendering state:
 *   - "loading"  → spinner + short copy (never blank)
 *   - "error"    → clear error + Retry / Sign in again buttons
 */
export function AuthLoading({ message = 'Preparing Velvet OS…' }) {
  return (
    <div
      data-testid="auth-loading-screen"
      className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background text-foreground"
    >
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      <div className="velvet-eyebrow text-foreground/60">Velvet OS</div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

export function AuthError({ message, onRetry, onSignOut }) {
  return (
    <div
      data-testid="auth-error-screen"
      className="min-h-screen flex items-center justify-center bg-background text-foreground p-6"
    >
      <div className="max-w-sm w-full border border-border bg-card p-8 space-y-5">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-[hsl(var(--muted-green-dark))]" />
          <div className="velvet-eyebrow text-foreground/60">Session issue</div>
        </div>
        <h2 className="font-serif text-2xl tracking-tight leading-tight">
          We couldn't reach Velvet OS.
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {message || 'The connection was interrupted. Retry or sign in again to continue.'}
        </p>
        <div className="flex flex-col gap-2 pt-2">
          <Button
            onClick={onRetry}
            className="w-full rounded-sm bg-foreground text-background hover:bg-foreground/90"
            data-testid="auth-retry-btn"
          >
            Retry connection
          </Button>
          <Button
            onClick={onSignOut}
            variant="ghost"
            className="w-full rounded-sm text-muted-foreground hover:text-foreground"
            data-testid="auth-signout-btn"
          >
            Sign in again
          </Button>
        </div>
      </div>
    </div>
  );
}
